import type { FormEvent, InputHTMLAttributes } from 'react';

import { classNames } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useRef, useState } from 'react';

import { isValidHexValue } from '../../../../utils';

import styles from './color-picker.module.css';

type Props = {
  'data-testid'?: string;
  onInput?: (value: string) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onInput'>;

export function ColorPicker({ id, className, style, 'data-testid': dataTestid, value, onInput, ...props }: Props) {
  const throttleTimeoutRef = useRef(0);
  const lastCallRef = useRef(0);
  const [localValue, setLocalValue] = useState(String(value ?? ''));
  const isValid = isValidHexValue(localValue);

  useEffect(() => setLocalValue(String(value ?? '')), [value]);

  useEffect(() => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallRef.current;

    if (timeSinceLastCall >= 80) {
      onInput?.(localValue);
      lastCallRef.current = now;
    } else {
      clearTimeout(throttleTimeoutRef.current);

      throttleTimeoutRef.current = window.setTimeout(() => {
        onInput?.(localValue);
        lastCallRef.current = Date.now();
      }, 80 - timeSinceLastCall);
    }

    return () => clearTimeout(throttleTimeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue]); // We don't expect onInput to change during the component lifecycle

  const handleInput = (event: FormEvent<HTMLInputElement>) => {
    setLocalValue(event.currentTarget.value);
  };

  return (
    <div className={classNames(styles.wrapper, className)} style={style}>
      <div
        className={classNames(styles.colorPreview, !isValid && styles.invalid)}
        style={isValid ? { backgroundColor: localValue } : undefined}
      >
        <input type="color" {...props} value={localValue} onInput={handleInput} />
      </div>

      <input id={id} type="text" data-testid={dataTestid} {...props} value={localValue} onInput={handleInput} />
    </div>
  );
}
